import React, { useState } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, TextField, Button,
  CircularProgress, Chip, Stack, Alert, MenuItem, Stepper, Step, StepLabel,
  Paper, Divider
} from '@mui/material';
import { School, CheckCircle, HowToReg, Send } from '@mui/icons-material';
import axios from 'axios';

const api = axios.create({ baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api' });
const SCHOOL_TYPES = ['primary','secondary','combined','technical','special'];
const ptLabel = v => (v||'').replace(/_/g,' ').replace(/\b\w/g,l=>l.toUpperCase());

export default function SchoolRegistration() {
  const [step, setStep] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    school_name:'', school_type:'secondary', school_code:'',
    address:'', city:'', region:'', country:'Eswatini',
    phone:'', email:'', website:'',
    admin_name:'', admin_email:'', admin_phone:'',
    student_count:'', teacher_count:'', motivation:''
  });
  const setF = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const next = () => { setError(''); setStep(s => s + 1); };
  const back = () => setStep(s => s - 1);

  const submit = async () => {
    setLoading(true); setError('');
    try {
      await api.post('/platform/register', form);
      setSubmitted(true);
    } catch (err) { setError(err.response?.data?.message || 'Submission failed. Please try again.'); }
    finally { setLoading(false); }
  };

  if (submitted) return (
    <Box sx={{ minHeight:'100vh', bgcolor:'#f5f7fa', display:'flex', alignItems:'center', justifyContent:'center', p:2 }}>
      <Card variant="outlined" sx={{ maxWidth:520, width:'100%', borderRadius:3, textAlign:'center', p:3 }}>
        <CheckCircle sx={{ fontSize:72, color:'#2e7d32', mb:2 }} />
        <Typography variant="h5" fontWeight={800} gutterBottom>Registration Submitted!</Typography>
        <Typography color="text.secondary" sx={{ mb:2 }}>
          Thank you for registering <strong>{form.school_name}</strong>. Our team will review your application and contact <strong>{form.admin_email}</strong> within 1–2 business days.
        </Typography>
        <Alert severity="info" sx={{ textAlign:'left', fontSize:12 }}>
          Once approved, you will receive your school's unique login URL and administrator credentials by email.
        </Alert>
      </Card>
    </Box>
  );

  const steps = ['School Details', 'Administrator', 'Review & Submit'];

  return (
    <Box sx={{ minHeight:'100vh', bgcolor:'#f5f7fa', py:4, px:2 }}>
      {/* Header */}
      <Box sx={{ textAlign:'center', mb:4 }}>
        <Stack direction="row" justifyContent="center" spacing={1.5} alignItems="center" sx={{ mb:1 }}>
          <School sx={{ fontSize:40, color:'#0f2b4a' }} />
          <Box>
            <Typography variant="h4" fontWeight={800} color="#0f2b4a">School Management Platform</Typography>
            <Typography color="text.secondary">Register your school to get started</Typography>
          </Box>
        </Stack>
      </Box>

      <Box sx={{ maxWidth:680, mx:'auto' }}>
        <Stepper activeStep={step} alternativeLabel sx={{ mb:4 }}>
          {steps.map(s => <Step key={s}><StepLabel>{s}</StepLabel></Step>)}
        </Stepper>

        {error && <Alert severity="error" sx={{ mb:2 }}>{error}</Alert>}

        <Card variant="outlined" sx={{ borderRadius:3 }}>
          <CardContent sx={{ p:3 }}>

            {/* Step 0: School details */}
            {step === 0 && (
              <Grid container spacing={2}>
                <Grid item xs={12}><Typography fontWeight={700} gutterBottom>School Information</Typography></Grid>
                <Grid item xs={12}><TextField fullWidth size="small" label="School Name *" value={form.school_name} onChange={setF('school_name')} /></Grid>
                <Grid item xs={6} sm={4}>
                  <TextField select fullWidth size="small" label="School Type" value={form.school_type} onChange={setF('school_type')}>
                    {SCHOOL_TYPES.map(t => <MenuItem key={t} value={t}>{ptLabel(t)}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={6} sm={4}><TextField fullWidth size="small" label="Ministry School Code" value={form.school_code} onChange={setF('school_code')} /></Grid>
                <Grid item xs={6} sm={4}><TextField fullWidth size="small" label="Country" value={form.country} onChange={setF('country')} /></Grid>
                <Grid item xs={6} sm={4}><TextField fullWidth size="small" label="Region / District" value={form.region} onChange={setF('region')} /></Grid>
                <Grid item xs={6} sm={4}><TextField fullWidth size="small" label="City / Town" value={form.city} onChange={setF('city')} /></Grid>
                <Grid item xs={12}><TextField fullWidth size="small" label="Physical Address" value={form.address} onChange={setF('address')} /></Grid>
                <Grid item xs={6}><TextField fullWidth size="small" label="Phone" value={form.phone} onChange={setF('phone')} /></Grid>
                <Grid item xs={6}><TextField fullWidth size="small" label="School Email" type="email" value={form.email} onChange={setF('email')} /></Grid>
                <Grid item xs={12}><TextField fullWidth size="small" label="Website (optional)" value={form.website} onChange={setF('website')} /></Grid>
                <Grid item xs={6}><TextField fullWidth size="small" type="number" label="Approx. Student Count" value={form.student_count} onChange={setF('student_count')} /></Grid>
                <Grid item xs={6}><TextField fullWidth size="small" type="number" label="Approx. Teacher Count" value={form.teacher_count} onChange={setF('teacher_count')} /></Grid>
              </Grid>
            )}

            {/* Step 1: Admin details */}
            {step === 1 && (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography fontWeight={700} gutterBottom>School Administrator</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb:2 }}>
                    This person will become the first admin user and can then create all other staff accounts.
                  </Typography>
                </Grid>
                <Grid item xs={12}><TextField fullWidth size="small" label="Full Name *" value={form.admin_name} onChange={setF('admin_name')} /></Grid>
                <Grid item xs={6}><TextField fullWidth size="small" label="Email Address *" type="email" value={form.admin_email} onChange={setF('admin_email')} /></Grid>
                <Grid item xs={6}><TextField fullWidth size="small" label="Phone" value={form.admin_phone} onChange={setF('admin_phone')} /></Grid>
                <Grid item xs={12}>
                  <TextField fullWidth size="small" label="Why are you interested in this platform?" value={form.motivation} onChange={setF('motivation')} multiline rows={4}
                    placeholder="Tell us about your school and what you hope to achieve with this system…" />
                </Grid>
              </Grid>
            )}

            {/* Step 2: Review */}
            {step === 2 && (
              <Box>
                <Typography fontWeight={700} gutterBottom>Review Your Application</Typography>
                <Grid container spacing={2}>
                  {[
                    ['School Name', form.school_name],
                    ['Type', ptLabel(form.school_type)],
                    ['Region', `${form.city || ''}${form.region ? `, ${form.region}` : ''}, ${form.country}`],
                    ['Phone', form.phone || '—'],
                    ['Email', form.email || '—'],
                    ['Students', form.student_count || '—'],
                    ['Administrator', form.admin_name],
                    ['Admin Email', form.admin_email],
                  ].map(([label, value]) => (
                    <Grid item xs={6} key={label}>
                      <Typography fontSize={11} color="text.secondary">{label}</Typography>
                      <Typography fontSize={13} fontWeight={600}>{value}</Typography>
                    </Grid>
                  ))}
                </Grid>
                <Divider sx={{ my:2 }} />
                <Alert severity="info" sx={{ fontSize:12 }}>
                  By submitting, you agree that a platform administrator will review your application. You will receive your login URL and credentials by email once approved.
                </Alert>
              </Box>
            )}
          </CardContent>
        </Card>

        <Stack direction="row" justifyContent="space-between" sx={{ mt:2 }}>
          <Button onClick={back} disabled={step === 0}>Back</Button>
          {step < 2 ? (
            <Button variant="contained" onClick={next}
              disabled={(step === 0 && !form.school_name) || (step === 1 && (!form.admin_name || !form.admin_email))}
              sx={{ bgcolor:'#0f2b4a' }}>Continue</Button>
          ) : (
            <Button variant="contained" startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <Send />}
              onClick={submit} disabled={loading} sx={{ bgcolor:'#2e7d32' }}>
              Submit Registration
            </Button>
          )}
        </Stack>
      </Box>
    </Box>
  );
}
